import { useQuery } from '@apollo/client'
import { AddCircle, Edit } from '@mui/icons-material'
import {
    Box,
    Button,
    IconButton,
    Link,
    Typography,
    useTheme,
} from '@mui/material'
import { gql } from '__generated__'
import { type GetCandidateStaffersQuery } from '__generated__/graphql'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import { CandidateStafferDrawer } from './CandidateStafferDrawer'

export const GetCandidateStaffers = gql(/* GraphQL */ `
    query GetCandidateStaffers($candidateId: ID) {
        candidate(id: $candidateId) {
            id
            staffers {
                id
                staffer {
                    id
                    firstName
                    lastName
                    title {
                        id
                        value
                    }
                    phone
                    email
                }
            }
        }
    }
`)

export type Staffer = NonNullable<
    NonNullable<
        NonNullable<GetCandidateStaffersQuery['candidate']>['staffers']
    >[number]['staffer']
>

interface CandidateStafferPanelProps {}

export const CandidateStafferPanel: React.FC<
    CandidateStafferPanelProps
> = () => {
    const theme = useTheme()
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const { candidateId } = useParams()
    const [staffers, setStaffers] = useState<Staffer[]>([])

    const { loading: isLoading } = useQuery(GetCandidateStaffers, {
        fetchPolicy: 'cache-and-network',
        variables: { candidateId },
        onCompleted: (data) => {
            setStaffers(
                data.candidate?.staffers
                    ?.map(({ staffer }) => staffer)
                    .filter((staffer) => !!staffer) || []
            )
        },
    })

    return (
        <>
            <CandidateStafferDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onCancel={() => setIsDrawerOpen(false)}
                staffers={staffers}
            />

            <ProfilePanel
                panelTitle="Staffer"
                {...(staffers.length > 0 && {
                    actionElements: [
                        <IconButton
                            key="edit-staffer"
                            onClick={() => setIsDrawerOpen(true)}
                            aria-label="Edit staffer"
                        >
                            <Edit fontSize="small" />
                        </IconButton>,
                    ],
                })}
            >
                {isLoading && (
                    <Box flex="1">
                        <LoadingPanel />
                    </Box>
                )}

                {!isLoading && staffers.length > 0 && (
                    <Box key={staffers[0].id}>
                        <Typography variant="subtitle2" noWrap>
                            {staffers[0].firstName} {staffers[0].lastName}
                        </Typography>

                        <Typography variant="body2" noWrap>
                            {staffers[0].title?.value}
                        </Typography>

                        <Typography variant="body2" noWrap>
                            {staffers[0].phone}
                        </Typography>

                        <Typography variant="body2" noWrap>
                            <Link
                                href={`mailto:${staffers[0].email}`}
                                sx={{
                                    textDecoration: 'none',
                                    color: theme.palette.primary.main,
                                }}
                            >
                                {staffers[0].email}
                            </Link>
                        </Typography>
                    </Box>
                )}

                {!isLoading && staffers.length < 1 && (
                    <>
                        <Box>
                            <Typography>-</Typography>
                        </Box>

                        <Button
                            onClick={() => setIsDrawerOpen(true)}
                            variant="text"
                            startIcon={<AddCircle />}
                            sx={{ marginTop: 'auto', alignSelf: 'flex-start' }}
                        >
                            Add Staffer
                        </Button>
                    </>
                )}
            </ProfilePanel>
        </>
    )
}
